import { accessToken, login } from '@/auth/oidc'
import type { CurrentUser } from '@/auth/permissions'

/**
 * HTTP-клиент к бэкенду (ТЗ §5.1).
 *
 * Токен подставляется здесь, а не в каждом вызове: забытый заголовок даёт 401, который
 * выглядит как «сессия истекла», и его чинят повторным входом вместо правки кода.
 */

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Ошибка от сервера с разобранным телом. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Отказ по правам, а не поломка: показывается иначе, чем 500. */
  get isForbidden(): boolean {
    return this.status === 403
  }
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Multipart or another already-encoded body. Mutually exclusive with body. */
  rawBody?: BodyInit
  /** Ключ идемпотентности для POST (ТЗ §6). */
  idempotencyKey?: string
  signal?: AbortSignal
}

/**
 * Запрос к API.
 *
 * 401 уводит на вход, а не выбрасывается наверх: истёкшая сессия — это не ошибка
 * приложения, и показывать её как красный тост означает предлагать пользователю
 * «повторить» то, что повториться не может.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await accessToken()

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

  const response = await fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.rawBody ??
      (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  })

  if (response.status === 401) {
    await login()
    // Уход на Keycloak — это навигация, промис никогда не разрешится. Бросаем, чтобы
    // вызывающий код не пытался разобрать несуществующий ответ.
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      problem?.code ?? null,
      problem?.detail ?? problem?.message ?? response.statusText,
    )
  }

  // 204 не имеет тела; response.json() на нём падает синтаксической ошибкой, которая
  // выглядит как сломанный ответ сервера.
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

/** Downloads bytes through the same authentication and RFC 7807 handling as JSON calls. */
export async function requestBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  const token = await accessToken()
  const headers: Record<string, string> = { Accept: 'application/octet-stream' }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`${BASE_URL}${path}`, { headers, signal })
  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      problem?.code ?? null,
      problem?.detail ?? problem?.message ?? response.statusText,
    )
  }
  return response.blob()
}

/** Кто я: роли и полный набор прав — основа меню и роутинга. */
export function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser> {
  return request<CurrentUser>('/me', { signal })
}
